import React, { useMemo, useState } from 'react';
import { Col, Row } from '../../lib/utils';
import { Body3Regular, CaptionRegular, Subtitle1Bold } from '../../lib/font';
import { colors } from '../../lib/colors';
import Grid, { GridColProps } from '../Grid/Grid';
import dayjs from 'dayjs';
import { useMembershipChangeLogs } from '@/features/membershipChangeLog/hooks';
import { ChangedField, ChangeLogActorType, ChangeLogTargetType } from '@/features/membershipChangeLog/types';

const ACTION_LABEL: Record<string, string> = {
    update: '수정',
    cancel: '취소/삭제',
    refund: '환불',
    upgrade: '업그레이드',
    convert: '그룹 전환',
    carryOut: '강사 따라 이동(보냄)',
    carryIn: '강사 따라 이동(받음)',
    currentsession: '회차 정정',
    commission: '커미션',
    // 자동 변동 (Booking 파생)
    reserve: '예약 차감',
    restore: '예약 취소(복원)',
    booking_cancel: '예약 취소(미복원)',
    noshow: '노쇼 차감',
};

const ACTOR_TYPE_LABEL: Record<ChangeLogActorType, string> = {
    admin: '관리자',
    coach: '강사',
    superAdmin: '슈퍼관리자',
    system: '시스템',
};

const ACTOR_TYPE_COLOR: Record<ChangeLogActorType, { bg: string; color: string }> = {
    admin: { bg: '#EAF1FE', color: '#3D73DD' },
    coach: { bg: '#E9F8EF', color: '#1F9254' },
    superAdmin: { bg: '#F3EAFE', color: '#7C3AED' },
    system: { bg: '#F1F5F9', color: '#64748B' },
};

const FIELD_LABEL: Record<string, string> = {
    carriedOut: '강사 따라 이동(보냄)',
    carriedIn: '강사 따라 이동(받음)',
    totalSession: '총 회차',
    lessonMovedOut: '수업 옮겨 나감',
    lessonMovedIn: '수업 옮겨 옴',
    lessonScheduleId: '옮긴 수업',
    lessonStartTime: '수업 시각',
    lessonMoveId: '옮기기 번호',
    lessonMoveOriginUnverified: '발급 기록 확인',
    startSessionValue: '시작 회차',
    remainingCount: '잔여 회차',
    totalPrice: '금액',
    pricePerSchedule: '회당 금액',
    perSessionFee: '회당 수업료',
    refundAmount: '환불액',
    activationDate: '시작일',
    expirationDate: '종료일',
    startDate: '시작일',
    expiryDate: '만료일',
    expiryPolicy: '만료 정책',
    status: '상태',
    isCanceled: '취소 여부',
};

const MONEY_FIELDS = new Set(['totalPrice', 'pricePerSchedule', 'perSessionFee', 'refundAmount']);
const DATE_FIELDS = new Set(['activationDate', 'expirationDate', 'startDate', 'expiryDate']);

const STATUS_LABEL: Record<string, string> = {
    active: '활성',
    expired: '만료',
    ACTIVE: '활성',
    EXPIRED: '만료',
    SUSPENDED: '정지',
    REFUNDED: '환불',
};

// 만료 정책 — 수강권 등록 폼(GroupTicketPage/ModalRegisterGroupTicket)과 동일 라벨
const EXPIRY_POLICY_LABEL: Record<string, string> = {
    FIRST: '둘 중 빠른 것',
    COUNT: '회차 소진 시',
    PERIOD: '기간 만료 시',
};

const formatVal = (field: string, value: any): string => {
    if (value === null || value === undefined || value === '') return '-';
    if (field === 'carriedOut') return '다른 센터로 이어보냄';
    if (field === 'carriedIn') return '다른 센터에서 이어받음';
    if (DATE_FIELDS.has(field)) {
        const d = dayjs(value);
        return d.isValid() ? d.format('YYYY-MM-DD') : String(value);
    }
    if (MONEY_FIELDS.has(field)) {
        const n = Number(value);
        return Number.isNaN(n) ? String(value) : `${n.toLocaleString()}원`;
    }
    if (field === 'isCanceled') return value ? '취소' : '정상';
    if (field === 'lessonMoveOriginUnverified') return value ? '관리자가 확인하고 옮김' : '-';
    if (field === 'status') return STATUS_LABEL[String(value)] || String(value);
    if (field === 'expiryPolicy') return EXPIRY_POLICY_LABEL[String(value)] || String(value);
    return String(value);
};

type Props = {
    targetType: ChangeLogTargetType;
    targetId: string;
    memberId?: string;
};

const MemberMembershipChangeLog = ({ targetType, targetId, memberId }: Props) => {
    const [includeSystem, setIncludeSystem] = useState(false);

    const { data, isLoading, isError } = useMembershipChangeLogs({
        targetType,
        targetId,
        memberId,
        includeSystem,
        enabled: !!targetId,
    });

    // 수업 옮기기 기록은 서버가 동시에 보충하면 같은 이동이 두 줄 생길 수 있다 — 옮기기 번호로 한 줄만 남긴다.
    const rows = useMemo(() => {
        const seen = new Set<string>();
        return (data?.results ?? []).filter((row: any) => {
            const moveId = row?.changedFields?.find?.((f: any) => f?.field === 'lessonMoveId')?.after;
            if (!moveId) return true;
            const scheduleId = row?.changedFields?.find?.((f: any) => f?.field === 'lessonScheduleId')?.after ?? '';
            const key = `${row.targetId ?? ''}|${scheduleId}|${moveId}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [data]);

    const colDefs = useMemo<GridColProps[]>(
        () => [
            {
                field: 'createdAt',
                headerName: '일시',
                type: 'date',
                dateFormat: 'YYYY-MM-DD HH:mm',
                align: 'center',
                width: 160,
            },
            {
                field: 'actorName',
                headerName: '작업자',
                align: 'center',
                width: 150,
                customComponent: (_value: any, row: any) => {
                    const actorType = (row?.actorType as ChangeLogActorType) || 'system';
                    const badge = ACTOR_TYPE_COLOR[actorType] || ACTOR_TYPE_COLOR.system;
                    return (
                        <Col style={{ alignItems: 'center', rowGap: 4 }}>
                            <div
                                style={{
                                    backgroundColor: badge.bg,
                                    color: badge.color,
                                    borderRadius: 6,
                                    padding: '2px 8px',
                                    fontSize: '1.2rem',
                                    fontWeight: 500,
                                }}
                            >
                                {ACTOR_TYPE_LABEL[actorType] || '시스템'}
                            </div>
                            <Body3Regular style={{ color: colors.LAVEL_3 }}>{row?.actorName || '-'}</Body3Regular>
                        </Col>
                    );
                },
            },
            {
                field: 'action',
                headerName: '작업',
                align: 'center',
                width: 110,
                customComponent: (value: any) => (
                    <Body3Regular style={{ color: colors.LAVEL_3 }}>{ACTION_LABEL[value] || value}</Body3Regular>
                ),
            },
            {
                field: 'changedFields',
                headerName: '변경 내용 (이전 → 이후)',
                align: 'left',
                width: 440,
                customComponent: (value: ChangedField[]) => {
                    const fields = Array.isArray(value) ? value : [];
                    if (fields.length === 0) {
                        return <Body3Regular style={{ color: colors.LAVEL_2 }}>-</Body3Regular>;
                    }
                    return (
                        <Col style={{ rowGap: 4, alignItems: 'flex-start' }}>
                            {fields.map((f, i) => (
                                <Row key={i} style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                    <Body3Regular style={{ color: colors.LAVEL_2, marginRight: 6 }}>
                                        {FIELD_LABEL[f.field] || f.field}
                                    </Body3Regular>
                                    <Body3Regular style={{ color: colors.LAVEL_2 }}>
                                        {formatVal(f.field, f.before)}
                                    </Body3Regular>
                                    <Body3Regular style={{ color: colors.LAVEL_3, margin: '0 6px' }}>→</Body3Regular>
                                    <Body3Regular style={{ color: '#3D73DD', fontWeight: 500 }}>
                                        {formatVal(f.field, f.after)}
                                    </Body3Regular>
                                </Row>
                            ))}
                        </Col>
                    );
                },
            },
            {
                field: 'reason',
                headerName: '사유',
                align: 'left',
                width: 180,
                nullValue: '-',
            },
        ],
        []
    );

    return (
        <Col>
            <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Subtitle1Bold>변경 이력</Subtitle1Bold>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={includeSystem}
                        onChange={(e) => setIncludeSystem(e.target.checked)}
                        style={{ marginRight: 6 }}
                    />
                    <CaptionRegular style={{ color: colors.LAVEL_2 }}>자동 변동 포함</CaptionRegular>
                </label>
            </Row>
            {isLoading ? (
                <Col style={{ alignItems: 'center', padding: '40px 0' }}>
                    <Body3Regular style={{ color: colors.LAVEL_2 }}>불러오는 중...</Body3Regular>
                </Col>
            ) : isError ? (
                <Col
                    style={{
                        alignItems: 'center',
                        padding: '40px 0',
                        border: '1px solid #E5E7EB',
                        borderRadius: 12,
                        backgroundColor: '#fff',
                    }}
                >
                    <Body3Regular style={{ color: colors.LAVEL_2 }}>변경 이력을 불러오지 못했습니다</Body3Regular>
                </Col>
            ) : rows.length === 0 ? (
                <Col
                    style={{
                        alignItems: 'center',
                        padding: '40px 0',
                        border: '1px solid #E5E7EB',
                        borderRadius: 12,
                        backgroundColor: '#fff',
                    }}
                >
                    <Body3Regular style={{ color: colors.LAVEL_2 }}>변경 이력이 없습니다</Body3Regular>
                </Col>
            ) : (
                <Grid
                    colDefs={colDefs}
                    rowData={rows}
                    isDisableHover
                    rightBottomComponent="dropdown"
                    pageSizeUnit="개"
                    pageSizeDropUp
                />
            )}
        </Col>
    );
};

export default MemberMembershipChangeLog;
